import React, { useMemo, useState } from 'react'
import MinimalisticTable, { ColumnDef }
  from '../../../../shared/components/ui/MinimalisticTable'
import { useTenants } from '../hooks/useTenants'
import type { Tenant } from '../api/tenantsApi'

import { useQuery } from '@tanstack/react-query'
import {
  fetchServicesApi,
  type Service,
} from '../../../system/services/api/servicesApi'

import CreateTenantDialog from './CreateTenantDialog'

type Row = Tenant & { __services_label: string }

export default function TenantsTable() {
  const [openCreate, setOpenCreate] = useState(false)

  // tenants
  const { data: tenants = [], isLoading, isError, error } = useTenants()

  // services (cached)
  const {
    data: services = [],
    isLoading: svcLoading,
    isError: svcError,
  } = useQuery<Service[], Error>({
    queryKey : ['services'],
    queryFn  : fetchServicesApi,
    staleTime: 300_000,
  })

  // id → name map
  const svcById = useMemo(
    () => new Map(services.map(s => [s.id_service, s.service_name] as const)),
    [services],
  )

  // computed rows (also provides a search-friendly label)
  const rows: Row[] = useMemo(
    () =>
      tenants.map(t => {
        const names = (t.service_ids ?? []).map(id => svcById.get(id) ?? `#${id}`)
        return { ...t, __services_label: names.join(', ') }
      }),
    [tenants, svcById],
  )

  const columns: ColumnDef<Row>[] = [
    { header: 'ID', accessor: 'id_tenant' },
    { header: 'Code', accessor: 'code' },
    {
      header: 'Display Name',
      accessor: 'display_name',
      Cell: (val: string | null) => (
        <span className="block max-w-[260px] truncate" title={val ?? ''}>
          {val ?? '—'}
        </span>
      ),
    },
    {
      header: 'Description',
      accessor: 'description',
      Cell: (val: string | null) => (
        <span className="block max-w-[320px] truncate" title={val ?? ''}>
          {val ?? '—'}
        </span>
      ),
    },
    {
      header: 'Active',
      accessor: 'active',
      Cell: (val: boolean | null) => (
        <span className={val ? 'text-emerald-400' : 'text-neutral-500'}>
          {val == null ? '—' : val ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      header: 'Services',
      accessor: '__services_label', // enables search by service names
      Cell: (_val, row) => {
        const ids = row.service_ids ?? []
        if (!ids.length) return <span className="text-neutral-500">—</span>
        if (svcLoading)  return <span className="text-neutral-500">Loading…</span>
        return (
          <div className="flex flex-wrap gap-1 max-w-[420px]">
            {ids.map(id => (
              <span
                key={id}
                className="px-2 py-0.5 text-xs rounded bg-neutral-800 text-neutral-200"
                title={svcById.get(id) ?? String(id)}
              >
                {svcById.get(id) ?? `#${id}`}
              </span>
            ))}
          </div>
        )
      },
    },
  ]

  if (isLoading) return <p className="text-neutral-400">Loading…</p>
  if (isError)   return <p className="text-rose-400">Error: {error?.message}</p>

  return (
    <>
      {svcError && (
        <p className="mb-2 text-amber-400 text-sm">
          Couldn’t load services — showing IDs instead.
        </p>
      )}

      <MinimalisticTable
        columns={columns}
        data={rows}
        noDataMessage="No tenants."
        showSearch
        searchPlaceholder="Search tenants or services…"
        showNewButton
        onNew={() => setOpenCreate(true)}
      />

      <CreateTenantDialog open={openCreate} onOpenChange={setOpenCreate} />
    </>
  )
}
